import { Fragment } from "react";
import { useTranslation } from "@1f/react-sdk";
import {
  TIPO_ANAGRAFICA_LEGALE_RAPP_KEY,
  TIPO_ANAGRAFICA_PROCURATORE_KEY,
} from "../../businessLogic/constants/baseTableKeys";
import Stack from "@vapor/react-material/Stack";
import Divider from "@vapor/react-material/Divider";
import Box from "@vapor/react-material/Box";
import IconButton from "@vapor/react-material/IconButton";
import FormGroup from "@vapor/react-material/FormGroup";
import FormControlLabel from "@vapor/react-material/FormControlLabel";
import Checkbox from "@vapor/react-material/Checkbox";
import Grid from "@vapor/react-material/Grid";
import TableContainer from "@vapor/react-material/TableContainer";
import Table from "@vapor/react-material/Table";
import TableBody from "@vapor/react-material/TableBody";
import TableRow from "@vapor/react-material/TableRow";
import TableCell from "@vapor/react-material/TableCell";
import Paper from "@vapor/react-material/Paper";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import {
  Add as AddIcon,
  ModeEdit as ModeEditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { NoData } from "../NoData";

export const AnagraficaSidebar = ({
  clientiList,
  collegateList,
  titolariList,
  lastSelectedAnag,
  tipoAnag,
  currentAnagraficaId,
  allUtentiList,
  isUserAllowedToPrivate,
  fascicolo,
  userPermissions,
  getTipoAnagIdForDelete,
  onSelectAnagrafica,
  onDeleteAnagrafica,
  onAddAnagrafica,
  onPrivateChange,
}) => {
  const { t } = useTranslation();

  const renderAnagRow = (item, tipo) => (
    <TableRow
      key={item.id}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      hover
      selected={
        lastSelectedAnag !== null &&
        tipoAnag === tipo &&
        (lastSelectedAnag.id === item.id || lastSelectedAnag.parentId === item.id)
      }
      style={
        tipo !== "cliente" &&
        item.anagraficaFascicoli.length > 0 &&
        item.anagraficaFascicoli.filter((af) =>
          tipo === "collegate"
            ? af.tipoAnagId === 3 || af.tipoAnagId === 4
            : af.tipoAnagId === 5
        )[0]?.dataFine !== undefined &&
        item.anagraficaFascicoli.filter((af) =>
          tipo === "collegate"
            ? af.tipoAnagId === 3 || af.tipoAnagId === 4
            : af.tipoAnagId === 5
        )[0]?.dataFine !== null
          ? { opacity: 0.5 }
          : {}
      }
    >
      <TableCell component="th" scope="row">
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Stack direction="column">
            <Stack direction="row" spacing={1}>
              {item.isPg ? <BusinessIcon /> : <PersonIcon />}
              <ExtendedTypography variant="titleSmall" color="primary.main">
                {item.isPg ? item.denominazione : item.nome + " " + item.cognome}
              </ExtendedTypography>
            </Stack>
            <ExtendedTypography variant="body" color="text.secondary">
              C.F.: {item.cf}
              {item.isPg ? " | P. Iva: " + item.pIva : null}
            </ExtendedTypography>
            <ExtendedTypography variant="body" color="text.secondary">
              {item.identifiedOn
                ? `Identificato in data ${new Date(item.identifiedOn).toLocaleDateString()}${
                    item.identifiedByUser
                      ? ` da ${allUtentiList.find((x) => x.userId === item.identifiedByUser)?.completeName || "Utente non trovato"}`
                      : ""
                  }`
                : "Non identificato"}
            </ExtendedTypography>
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row">
            <IconButton
              edge="end"
              aria-label="select"
              color="primary"
              onClick={() => onSelectAnagrafica(item, tipo)}
              sx={{ mr: "1px !important" }}
            >
              <ModeEditIcon fontSize="small" />
            </IconButton>
            {tipo !== "cliente" && userPermissions.canDelete ? (
              <IconButton
                edge="end"
                aria-label="delete"
                color="error"
                onClick={() =>
                  onDeleteAnagrafica(
                    item,
                    tipo === "collegate"
                      ? getTipoAnagIdForDelete(item, [TIPO_ANAGRAFICA_LEGALE_RAPP_KEY, TIPO_ANAGRAFICA_PROCURATORE_KEY])
                      : 5
                  )
                }
                sx={{ mr: "1px !important" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <Stack direction="column" spacing={1} sx={{ width: "25%" }}>
      <Grid container justifyContent="left" alignItems="center">
        {isUserAllowedToPrivate ? (
          <Fragment>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="chkPrivate"
                      checked={fascicolo.isPrivate}
                      onChange={onPrivateChange}
                    />
                  }
                  label={t("fascicolo.form.privato")}
                />
              </FormGroup>
              <Divider variant="standard" sx={{ pt: 1 }} />
            </Grid>
          </Fragment>
        ) : null}
      </Grid>

      <Stack direction="row" spacing={1}>
        <ExtendedTypography variant="titleSmall" color="primary.main">
          {t("anagrafica.anagraficaForm.cli")}
        </ExtendedTypography>
      </Stack>
      <Divider variant="standard" />

      {clientiList.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>{clientiList.map((item) => renderAnagRow(item, "cliente"))}</TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoData size="subtitle" />
      )}

      <Divider variant="standard" />

      <Stack direction="row" spacing={1}>
        <ExtendedTypography variant="titleSmall" color="primary.main">
          {t("anagrafica.anagraficaForm.collAnag")}
        </ExtendedTypography>
        <Box flexGrow={1} />
        {userPermissions.canCreate && (
          <IconButton
            color="primary"
            size="small"
            sx={{ mr: 2 }}
            onClick={() => onAddAnagrafica("collegate")}
            readOnly={currentAnagraficaId === 0}
          >
            <AddIcon />
          </IconButton>
        )}
      </Stack>
      <Divider variant="standard" />

      {collegateList.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>{collegateList.map((item) => renderAnagRow(item, "collegate"))}</TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoData size="subtitle" />
      )}

      <Divider variant="standard" />

      <Stack direction="row" spacing={1}>
        <ExtendedTypography variant="titleSmall" color="primary.main">
          {t("anagrafica.anagraficaForm.titEff")}
        </ExtendedTypography>
        <Box flexGrow={1} />
        {userPermissions.canCreate && (
          <IconButton
            color="primary"
            size="small"
            sx={{ mr: 2 }}
            onClick={() => onAddAnagrafica("titolare")}
            readOnly={currentAnagraficaId === 0}
          >
            <AddIcon />
          </IconButton>
        )}
      </Stack>
      <Divider variant="standard" />

      {titolariList.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>{titolariList.map((item) => renderAnagRow(item, "titolare"))}</TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoData size="subtitle" />
      )}
    </Stack>
  );
};
